/**
 * "NÃO RECEBI UM E-MAIL" — o diagnóstico do que ENTRA, para quem usa a caixa.
 *
 * O nome da tela é a frase que a pessoa diria em voz alta. "Quarentena" é
 * como nós chamamos; ninguém abre o webmail pensando "vou ver a quarentena".
 *
 * ELA RESPONDE A PERGUNTA INTEIRA, não um terço dela. Quem não recebeu algo
 * tem três destinos possíveis, e mostrar só o primeiro faria a tela parecer
 * quebrada nos outros dois:
 *
 *   1. chegou e foi separada como spam   → está aqui, com o motivo e os botões
 *   2. foi recusada antes de entrar      → está aqui, com o que fazer
 *   3. nunca chegou até o servidor       → é o estado VAZIO, e ele ensina
 *
 * O terceiro é o que mais importa. Uma lista vazia sem explicação faz quem
 * está com um problema concluir que a ferramenta quebrou — quando a resposta
 * certa é "não chegou aqui; o próximo passo é com quem enviou".
 *
 * FRONTEIRA ENTRE CLIENTES (regra 5): nenhum filtro daqui garante isolamento.
 * Quem garante é a API — `escopoDeCaixas` limita a quarentena à caixa de quem
 * pede, e `escopoDeEventos` limita as entregas ao que tem o e-mail dele como
 * remetente ou destinatário. Esta tela só escolhe o LADO (`para`), nunca o
 * dono.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { entregas, quarentena } from "../api/admin";
import { explicar } from "../api/problema";
import { useCaixa } from "../sessao/contexto";
import { Icone } from "../ui/Icone";
import { dataCurta } from "../ui/formato";
import { DIAS_PADRAO, desdeQuando } from "./periodo";

/** Quantos dias para trás a busca olha. */
const PERIODOS = [
  { rotulo: "Últimos 7 dias", dias: 7 },
  { rotulo: "Últimos 30 dias", dias: 30 },
];

export function NaoRecebi() {
  const eu = useCaixa();
  const fila = useQueryClient();
  const [de, setDe] = useState("");
  const [dias, setDias] = useState(DIAS_PADRAO);
  // A busca só vale quando a pessoa MANDA buscar. Filtrar a cada tecla faria
  // a lista piscar e disparar uma consulta por letra.
  const [busca, setBusca] = useState({ de: "", dias: DIAS_PADRAO });
  const [erro, setErro] = useState("");

  const retidas = useQuery({
    queryKey: ["nao-recebi", "quarentena", busca.de],
    queryFn: () => quarentena.listar({ por_pagina: 50 }),
  });

  const recusadas = useQuery({
    queryKey: ["nao-recebi", "recusadas", busca],
    queryFn: () =>
      entregas.listar({
        por_pagina: 50,
        // `para`, e não `endereco`: aqui só interessa o que vinha PARA esta
        // caixa. Com `endereco`, o que a pessoa enviou apareceria junto.
        para: eu.caixa,
        de: busca.de || undefined,
        desde: desdeQuando(busca.dias),
      }),
  });

  const porNome = (s: string) =>
    !busca.de || s.toLowerCase().includes(busca.de.toLowerCase());

  const listaRetidas = (retidas.data?.itens ?? []).filter((m) =>
    porNome(m.remetente ?? ""),
  );
  // Entrega bem-sucedida não entra: quem abriu esta tela já sabe que não
  // encontrou a mensagem na Caixa de entrada.
  // ORDENADA, DO MAIS RECENTE PARA O MAIS ANTIGO.
  //
  // Vinha na ordem do servidor e aparecia 08:43, 08:48, 08:13 — e numa tela
  // que existe para responder "cadê a mensagem que acabou de chegar?", a
  // mais nova tem que estar em cima. Fora de ordem, a pessoa lê a lista
  // inteira para achar o que está no começo.
  const listaRecusadas = (recusadas.data?.itens ?? [])
    .filter((e) => e.tipo !== "entregue" && e.tipo !== "quarentena")
    .slice()
    .sort((a, b) => (a.ocorrido_em ?? "") < (b.ocorrido_em ?? "") ? 1 : -1);

  const decidir = useMutation({
    mutationFn: ({ uuid, ehSpam }: { uuid: string; ehSpam: boolean }) =>
      ehSpam ? quarentena.descartar(uuid) : quarentena.liberar(uuid),
    onSuccess: () => {
      void fila.invalidateQueries({ queryKey: ["nao-recebi"] });
      // Liberar move a mensagem para a Caixa de entrada: a lista de lá e os
      // contadores das pastas ficam velhos na mesma hora.
      void fila.invalidateQueries({ queryKey: ["mensagens"] });
      void fila.invalidateQueries({ queryKey: ["pastas"] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  const carregando = retidas.isLoading || recusadas.isLoading;
  const achouAlgo = listaRetidas.length > 0 || listaRecusadas.length > 0;

  return (
    /* O QUE ROLA É O CONTEÚDO, NÃO A PÁGINA.
       A revisão de UX de 30/10/2026 apontou "layout não contido na altura da
       tela, em todas as telas". Estas duas eram as que de fato escapavam: o
       esqueleto do webmail tem altura fixa (`h-[100dvh]`) e espera que cada
       tela filha seja um item de flex que rola por dentro — é o que Lista,
       Leitura, Busca e Contatos fazem. Aqui a raiz era só um `mx-auto`, sem
       `flex-1` e sem rolagem: com muitos eventos listados, o conteúdo
       transbordava o esqueleto por baixo, fora de alcance, e nenhuma barra de
       rolagem aparecia para chegar nele.
       `min-w-0` porque a tabela de eventos é larga: sem ele, um destinatário
       comprido alarga este item de flex e empurra a barra lateral. */
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <h1 className="text-xl font-semibold text-tinta sm:text-2xl">
          Não recebi um e-mail
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-tinta-media">
          Alguém diz que te mandou algo e não chegou? Veja o que aconteceu com as
          mensagens endereçadas a <strong className="text-tinta">{eu.caixa}</strong>.
        </p>

        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            setErro("");
            setBusca({ de: de.trim(), dias });
          }}
          className="mt-5 flex flex-wrap items-end gap-3 rounded-2xl border border-linha bg-white p-4"
        >
          <label className="min-w-[240px] flex-1 text-xs text-tinta-fraca">
            De quem era o e-mail?
            <input
              value={de}
              onChange={(ev) => setDe(ev.target.value)}
              placeholder="nome ou endereço — em branco mostra tudo"
              className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
            />
          </label>
          <label className="text-xs text-tinta-fraca">
            Quando
            <select
              value={dias}
              onChange={(ev) => setDias(Number(ev.target.value))}
              className="mt-1 block rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
            >
              {PERIODOS.map((p) => (
                <option key={p.dias} value={p.dias}>
                  {p.rotulo}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="rounded-full bg-acao px-5 py-2.5 text-sm font-medium text-white"
          >
            Procurar
          </button>
        </form>

        {erro && (
          <p className="mt-3 rounded-lg border border-perigo/40 bg-perigo/10 px-3 py-2 text-sm text-tinta">
            {erro}
          </p>
        )}

        {carregando && (
          <p className="mt-6 text-sm text-tinta-fraca">Procurando…</p>
        )}

        {!carregando && listaRetidas.length > 0 && (
          <section className="mt-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-tinta">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-aviso/20">
                <Icone nome="AlertTriangle" tamanho={13} className="text-aviso" />
              </span>
              Chegou, mas foi separado como spam
              <span className="font-normal text-tinta-fraca">
                — {listaRetidas.length}{" "}
                {listaRetidas.length === 1 ? "mensagem" : "mensagens"}
              </span>
            </h2>

            {listaRetidas.map((m) => (
              <article
                key={m.uuid}
                className="mt-2 rounded-xl border border-linha border-l-[3px] border-l-aviso bg-white p-4"
              >
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <strong className="text-sm text-tinta">{m.remetente}</strong>
                  <span className="min-w-0 flex-1 text-sm text-tinta-media">
                    {m.assunto || "(sem assunto)"}
                  </span>
                  <span className="text-xs text-tinta-fraca">
                    {dataCurta(m.recebida_em)}
                  </span>
                </div>

                {/* O PORQUÊ, em frase. Os símbolos crus do antispam continuam na
                    resposta para o painel técnico; aqui entram traduzidos. */}
                {(m.motivos_explicados ?? []).length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {(m.motivos_explicados ?? []).map((frase, i) => (
                      <li key={i} className="text-sm text-tinta-media">
                        • {frase}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-tinta-media">
                    O filtro achou a mensagem parecida com spam, sem um motivo
                    único que valha destacar.
                  </p>
                )}

                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    disabled={decidir.isPending}
                    onClick={() =>
                      decidir.mutate({ uuid: m.uuid!, ehSpam: false })
                    }
                    className="rounded-full bg-acao px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
                  >
                    Não é spam — pôr na Caixa de entrada
                  </button>
                  <button
                    type="button"
                    disabled={decidir.isPending}
                    onClick={() => decidir.mutate({ uuid: m.uuid!, ehSpam: true })}
                    className="rounded-full border border-linha px-4 py-2 text-sm text-tinta-media disabled:opacity-40"
                  >
                    É spam mesmo
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}

        {!carregando && listaRecusadas.length > 0 && (
          <section className="mt-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-tinta">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-perigo/15">
                <Icone nome="X" tamanho={13} className="text-perigo" />
              </span>
              Recusado antes de entrar
              <span className="font-normal text-tinta-fraca">
                — {listaRecusadas.length}
              </span>
            </h2>

            {listaRecusadas.map((e) => (
              <article
                key={e.uuid}
                className="mt-2 rounded-xl border border-linha border-l-[3px] border-l-perigo bg-white p-4"
              >
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <strong className="text-sm text-tinta">{e.remetente}</strong>
                  <span className="ml-auto text-xs text-tinta-fraca">
                    {dataCurta(e.ocorrido_em)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-tinta-media">{e.explicacao}</p>
                {e.acao && (
                  <p className="mt-2 border-l-2 border-linha pl-3 text-xs text-tinta-fraca">
                    <strong className="text-tinta-media">O que fazer:</strong>{" "}
                    {e.acao}
                  </p>
                )}
              </article>
            ))}
          </section>
        )}

        {/* O ESTADO VAZIO É O QUE MAIS IMPORTA.

            Quem chegou aqui tem um problema. Uma lista vazia sem explicação faz
            essa pessoa concluir que a ferramenta quebrou — quando a resposta
            certa é que a mensagem não chegou ao servidor, e o próximo passo é
            com quem enviou. Dizer ONDE procuramos e POR QUANTO TEMPO é o que
            transforma "nada aqui" em informação. */}
        {!carregando && (
          <section className="mt-6 rounded-xl border border-dashed border-linha bg-white p-4">
            <h2 className="text-sm font-semibold text-tinta">
              {achouAlgo ? "E nada mais foi encontrado" : "Nada foi encontrado"}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-tinta-media">
              Procuramos em tudo que chegou ao servidor nos{" "}
              <strong className="text-tinta">últimos {busca.dias} dias</strong> —
              entregue, retido ou recusado
              {busca.de && (
                <>
                  , vindo de <strong className="text-tinta">{busca.de}</strong>
                </>
              )}
              . Se o e-mail que você espera não está acima,{" "}
              <strong className="text-tinta">ele não chegou até aqui</strong>.
            </p>
            <p className="mt-2 max-w-2xl text-sm text-tinta-media">
              O próximo passo é com quem enviou: peça para a pessoa conferir se
              recebeu uma devolução automática. É ela que diz onde a mensagem
              parou.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
